/* ==========================================================================
   Runtime configuration.

   RULE: nothing in this file, or anything it reads, may be a secret.
   Payment secret keys, vending API credentials, database credentials and
   any private API key must live on the backend ONLY. This file may only
   ever hold values that are safe to ship inside a public JS bundle: base
   URLs, public/publishable keys, feature flags.

   Values are read from `window.__PROPERVEST_ENV__`, which a real deployment
   would populate via a small injected <script> at deploy time (or a build
   step's define/replace step) — NOT by baking secrets into this repo.
   ========================================================================== */

interface PublicEnv {
  API_BASE_URL?: string;
  USE_MOCK_SERVICES?: boolean;
  /** Supabase project URL and anon/public key. Both are safe to expose in
   *  the frontend bundle — the anon key has no power on its own, every
   *  table and function is gated by the Row Level Security policies and
   *  SECURITY DEFINER checks in supabase/schema.sql. Never put the
   *  service_role key here or anywhere in frontend code. */
  SUPABASE_URL?: string;
  SUPABASE_ANON_KEY?: string;
}

declare global {
  interface Window {
    __PROPERVEST_ENV__?: PublicEnv;
  }
}

const env: PublicEnv = (typeof window !== "undefined" && window.__PROPERVEST_ENV__) || {};

const hasSupabaseCredentials = Boolean(env.SUPABASE_URL && env.SUPABASE_ANON_KEY);

// Escape hatch for testing: ?mock=1 forces mock mode even when real
// Supabase credentials are embedded in the page, so the mock-data flows
// can still be exercised (e.g. by an automated test) without touching the
// real database. Has no effect once USE_MOCK_SERVICES is set explicitly.
const forceMockViaQuery =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).get("mock") === "1";

export const config = {
  /** Backend base URL. Empty string keeps apiClient inert (mock mode) until
   *  a real backend exists. */
  apiBaseUrl: env.API_BASE_URL ?? "",

  supabaseUrl: env.SUPABASE_URL ?? "",
  supabaseAnonKey: env.SUPABASE_ANON_KEY ?? "",

  /** Master switch: while true, every service (except payment — see
   *  paymentProviderConnected) resolves through the mock implementations
   *  in src/services/mock/. Defaults to real mode automatically the
   *  moment Supabase credentials are present, so there's nothing extra to
   *  remember to flip. Can still be forced to true even with credentials
   *  set, via ?mock=1 or by setting USE_MOCK_SERVICES explicitly. */
  useMockServices: env.USE_MOCK_SERVICES ?? (forceMockViaQuery || !hasSupabaseCredentials),

  /** Separate from useMockServices on purpose: the data backend (auth,
   *  wallet, meters, bills, transactions) can go live on Supabase while
   *  the payment provider is still unconnected. paymentService checks
   *  this flag, not useMockServices, so funding the wallet stays
   *  simulated until a payment provider is wired up — flip this
   *  only once that milestone starts. */
  paymentProviderConnected: false,

  /** QA-only hook: append ?mockFail=1 to the URL to force the mock payment
   *  and vending services down their failure path, so the pending/failed
   *  UI states can be reviewed without waiting for real failures. Has no
   *  effect once useMockServices is false. */
  forceMockFailure:
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("mockFail") === "1",

  storageKeys: {
    session: "propervest.session.v1",
  },
} as const;
